'use client';

import { useEffect, useRef, type ReactNode, type MouseEvent } from 'react';

interface VendorResultImpressionsProps {
  children: ReactNode;
  className?: string;
  /** Changes on a new search, filter, sort or page. Never sent to analytics. */
  viewKey: string;
  endpoint: string;
  actionEndpoint: string;
  category?: string;
  surface?: 'vendor_results' | 'vendor_category_results';
}

/** Counts actual viewport exposure; rendering/prefetch alone is not a view. */
export function VendorResultImpressions({
  children,
  className,
  viewKey,
  endpoint,
  actionEndpoint,
  category,
  surface = 'vendor_results',
}: VendorResultImpressionsProps) {
  const root = useRef<HTMLDivElement>(null);
  const visit = useRef<{ key: string; operationId: string; sent: Set<string> } | null>(null);

  const visitKey = JSON.stringify([endpoint, actionEndpoint, surface, category, viewKey]);
  function currentVisit() {
    if (!visit.current || visit.current.key !== visitKey) {
      visit.current = { key: visitKey, operationId: crypto.randomUUID(), sent: new Set() };
    }
    return visit.current;
  }
  async function sendEvent(url: string, payload: Record<string, unknown>) {
    const body = JSON.stringify(payload);
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
        if (response.ok) return;
        if (response.status < 500) break;
      } catch { /* Retry the same stable event id once. */ }
    }
    console.error('[vendor-results] Event capture failed');
  }
  function trackAction(event: MouseEvent<HTMLDivElement>) {
    if (document.visibilityState !== 'visible' || event.button > 1) return;
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-vendor-action]') : null;
    const card = target?.closest<HTMLElement>('[data-vendor-result-id]');
    if (!target || !card) return;
    const businessProfileId = Number(card.dataset.vendorResultId);
    const position = Number(card.dataset.resultPosition);
    if (!Number.isSafeInteger(businessProfileId) || businessProfileId < 1 || !Number.isSafeInteger(position) || position < 1) return;
    void sendEvent(actionEndpoint.replace('{vendorId}', String(businessProfileId)), {
      type: target.dataset.vendorAction, eventId: crypto.randomUUID(), businessProfileId,
      position, surface, category, operationId: currentVisit().operationId,
    });
  }

  useEffect(() => {
    if (!endpoint || !root.current || typeof IntersectionObserver === 'undefined') return;
    const activeVisit = currentVisit();
    const cards = Array.from(root.current.querySelectorAll<HTMLElement>('[data-vendor-result-id]'));
    const observer = new IntersectionObserver((entries) => {
      if (document.visibilityState !== 'visible') return;
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const card = entry.target as HTMLElement;
        const businessProfileId = Number(card.dataset.vendorResultId);
        const position = Number(card.dataset.resultPosition);
        if (!Number.isSafeInteger(businessProfileId) || businessProfileId < 1 ||
            !Number.isSafeInteger(position) || position < 1) continue;
        const key = String(businessProfileId);
        if (activeVisit.sent.has(key)) continue;
        activeVisit.sent.add(key);
        observer.unobserve(card);
        void sendEvent(endpoint, {
          businessProfileId, position, surface, category, operationId: activeVisit.operationId,
        });
      }
    }, { threshold: 0.5 });
    const observeRemaining = () => {
      if (document.visibilityState !== 'visible') return;
      for (const card of cards) {
        if (!activeVisit.sent.has(card.dataset.vendorResultId ?? '')) {
          observer.unobserve(card);
          observer.observe(card);
        }
      }
    };
    observeRemaining();
    document.addEventListener('visibilitychange', observeRemaining);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', observeRemaining);
    };
  // visitKey includes endpoint/tenant and category; a changed result operation resets exposure.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitKey]);

  return <div ref={root} className={className} onClickCapture={trackAction} onAuxClickCapture={trackAction}>{children}</div>;
}
